import type { CalculateFormValues } from '@innogy/become-a-customer/shared';
import {
  validateElectricity,
  validateGas,
} from '@innogy/become-a-customer/shared';
import type { InnogyComponentRendering } from '@core/jss-models';
import { getFieldValue } from '@core/jss-utils';
import type { Action } from '@ngrx/store';
import { createReducer, on } from '@ngrx/store';
import type { FormGroupState } from 'ngrx-forms';
import {
  createFormGroupState,
  markAsDirty,
  markAsTouched,
  onNgrxForms,
  onNgrxFormsAction,
  reset,
  ResetAction,
  setValue,
  wrapReducerWithFormStateUpdate,
} from 'ngrx-forms';

import {
  addCalculationRendering,
  contractIncludesGasInitializationRequested,
  submitCalculateForm,
} from './calculate-form.actions';

export const CALCULATE_FORM_ID = 'calculateForm';

export const gasUsageFormControlId = `${CALCULATE_FORM_ID}.gasUsage`;
export const electricityUsageFormControlId = `${CALCULATE_FORM_ID}.electricityUsage`;
export const electricityUsageNormalFormControlId = `${CALCULATE_FORM_ID}.electricityUsageNormal`;
export const electricityUsageOffPeakFormControlId = `${CALCULATE_FORM_ID}.electricityUsageOffPeak`;
export const electricityReturnFormControlId = `${CALCULATE_FORM_ID}.electricityReturn`;
export const doubleMeterFormControlId = `${CALCULATE_FORM_ID}.doubleMeter`;
export const hasSolarPanelFormControlId = `${CALCULATE_FORM_ID}.hasSolarPanel`;
export const existingCustomerFormControlId = `${CALCULATE_FORM_ID}.existingCustomer`;

export interface CalculateFormState {
  formState: FormGroupState<CalculateFormValues>;
  rendering?: InnogyComponentRendering | any;
  contractIncludesGas: boolean;
}

const intitialFormValues = {
  gasUsage: NaN,
  electricityUsage: NaN,
  electricityUsageNormal: NaN,
  electricityUsageOffPeak: NaN,
  electricityReturn: NaN,
  doubleMeter: false,
  hasSolarPanel: false,
  existingCustomer: false,
};

export const initialFormState = createFormGroupState<CalculateFormValues>(
  CALCULATE_FORM_ID,
  intitialFormValues
);

export const initialState: CalculateFormState = {
  formState: initialFormState,
  rendering: undefined,
  contractIncludesGas: false,
};

export const getRenderingValue =
  <T>(key: string, defaultValue: T) =>
  (state: CalculateFormState) => {
    return getFieldValue<T>(state.rendering, key, defaultValue);
  };

const validateForm = (state: CalculateFormState) => {
  const isElectricityRequired = getRenderingValue(
    'ElectricityUsageRequired',
    false
  )(state);

  return validateElectricity(
    isElectricityRequired,
    state.formState
  )(validateGas(state.contractIncludesGas, state.formState)(state.formState));
};

const _reducer = createReducer(
  initialState,
  onNgrxForms(),
  on(submitCalculateForm, (state) => {
    let formState = markAsDirty(state.formState);
    formState = markAsTouched(formState);

    return {
      ...state,
      formState,
    };
  }),
  onNgrxFormsAction(ResetAction, (state, action) => {
    if (action.controlId === CALCULATE_FORM_ID) {
      const newFormState = setValue(state.formState, intitialFormValues);

      return {
        ...state,
        formState: reset(newFormState),
      };
    }
    return state;
  }),
  on(addCalculationRendering, (state, action) => {
    return { ...state, rendering: action.payload };
  }),
  on(
    contractIncludesGasInitializationRequested,
    (state, { contractIncludesGas }) => ({ ...state, contractIncludesGas })
  )
);

const wrappedReducer = wrapReducerWithFormStateUpdate(
  _reducer,
  (state) => state.formState,
  (_, state) => validateForm(state)
);

export function calculateFormReducer(
  state: CalculateFormState = initialState,
  action: Action
): CalculateFormState {
  return wrappedReducer(state, action);
}
